import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import {
  Dialog,
  DialogContent,
} from '../ui/dialog';
import { X, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import api from '../../lib/axios';
import { cn } from '../../lib/utils';

// Popup Announcement Component - Modern Design
export const AnnouncementPopup = () => {
  const { user, isAuthenticated } = useAuth();
  const [announcement, setAnnouncement] = useState(null);
  const [dismissed, setDismissed] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    
    const fetchPopup = async () => {
      try {
        // Get dismissed list
        const dismissedRes = await api.get('/settings/announcements/dismissed');
        const dismissedIds = dismissedRes.data?.dismissed_ids || [];
        setDismissed(dismissedIds);
        
        // Get popup announcements
        const res = await api.get('/settings/announcements', { params: { display_type: 'popup' } });
        const popups = res.data || [];
        
        // Find first non-dismissed popup
        const activePopup = popups.find(p => !dismissedIds.includes(p.id));
        if (activePopup) {
          setAnnouncement(activePopup);
          setOpen(true);
        }
      } catch (error) {
        console.error('Error fetching popup:', error);
      }
    };
    
    // Delay popup display
    const timer = setTimeout(fetchPopup, 1500);
    return () => clearTimeout(timer);
  }, [isAuthenticated]);

  const handleDismiss = async (dontShowAgain = false) => {
    if (dontShowAgain && announcement) {
      try {
        await api.post(`/settings/announcements/${announcement.id}/dismiss`);
      } catch (error) {
        console.error('Error dismissing:', error);
      }
    }
    setOpen(false);
  };

  const extractYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&?\s]+)/);
    return match ? match[1] : null;
  };

  if (!announcement) return null;

  const youtubeId = announcement.media_type === 'video_youtube' ? extractYouTubeId(announcement.media_url) : null;
  const bgColor = announcement.background_color || '#ffffff';
  const textColor = announcement.text_color || '#1a365d';

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent 
        className="sm:max-w-lg p-0 overflow-hidden border-0 shadow-2xl rounded-2xl" 
        hideClose
        data-testid="announcement-popup"
      >
        <div className="relative">
          {/* Single Close button - Modern Style */}
          <button
            onClick={() => handleDismiss(false)}
            className="absolute top-3 right-3 z-20 w-8 h-8 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center text-white transition-all hover:scale-110 backdrop-blur-sm"
            data-testid="popup-close-btn"
          >
            <X className="w-4 h-4" />
          </button>
          
          {/* Media Section */}
          {announcement.media_type === 'image' && announcement.media_url && (
            <div className="relative overflow-hidden">
              <img
                src={announcement.media_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${announcement.media_url}` : announcement.media_url}
                alt=""
                className="w-full max-h-56 object-cover"
              />
              {/* Gradient overlay for smooth transition */}
              <div 
                className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t to-transparent"
                style={{ background: `linear-gradient(to top, ${bgColor}, transparent)` }}
              />
            </div>
          )}
          
          {youtubeId && (
            <div className="aspect-video">
              <iframe
                src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1&mute=1`}
                title="YouTube video"
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}
          
          {/* Content Section - Modern Style */}
          <div 
            className="p-6 pt-4"
            style={{ backgroundColor: bgColor, color: textColor }}
          >
            <h2 className="text-xl font-bold mb-3 leading-tight">{announcement.title}</h2>
            {announcement.content && (
              <p className="text-sm opacity-80 leading-relaxed mb-5">{announcement.content}</p>
            )}
            
            {/* Actions - Single row with CTA button */}
            <div className="flex items-center justify-between gap-3">
              {announcement.action_button_text && announcement.action_button_url ? (
                <Button
                  onClick={() => {
                    window.open(announcement.action_button_url, '_blank');
                    handleDismiss(false);
                  }}
                  className="flex-1 rounded-xl h-11 font-medium transition-all hover:scale-[1.02]"
                  style={{ 
                    backgroundColor: textColor,
                    color: bgColor
                  }}
                >
                  {announcement.action_button_text}
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <div className="flex-1" />
              )}
              
              <button
                onClick={() => handleDismiss(true)}
                className="text-xs opacity-50 hover:opacity-80 transition-opacity whitespace-nowrap"
              >
                Ne plus afficher
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Carousel / Flash Infos Component - Enhanced with media background, overlay, and video support
export const FlashInfosCarousel = ({ className }) => {
  const { isAuthenticated } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = React.useRef(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    
    const fetchCarousel = async () => {
      try {
        const res = await api.get('/settings/announcements', { params: { display_type: 'carousel' } });
        setAnnouncements(res.data || []);
      } catch (error) {
        console.error('Error fetching carousel:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchCarousel();
  }, [isAuthenticated]);

  // Auto-slide
  useEffect(() => {
    if (announcements.length <= 1 || isPaused) return;
    
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % announcements.length);
    }, 6000);
    
    return () => clearInterval(interval);
  }, [announcements.length, isPaused]);

  // Handle video autoplay when slide changes
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(() => {});
    }
  }, [currentIndex]);

  const goTo = (index) => setCurrentIndex(index);
  const goPrev = () => setCurrentIndex(prev => prev === 0 ? announcements.length - 1 : prev - 1);
  const goNext = () => setCurrentIndex(prev => (prev + 1) % announcements.length);
  const toggleMute = () => {
    setIsMuted(!isMuted);
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
    }
  };

  if (loading || announcements.length === 0) return null;

  const current = announcements[currentIndex];
  
  // Media configuration
  const hasBackgroundImage = current.media_type === 'image' && current.media_url;
  const hasBackgroundVideo = current.media_type === 'video' && current.media_url;
  const hasMedia = hasBackgroundImage || hasBackgroundVideo;
  
  // Colors and overlay
  const bgColor = current.background_color || '#1a365d';
  const textColor = current.text_color || '#ffffff';
  const overlayColor = current.overlay_color || bgColor;
  const overlayOpacity = current.overlay_opacity ?? 0.6;
  
  // Banner height
  const bannerHeight = current.banner_height || 'medium';
  const heightClass = {
    compact: 'min-h-[100px]',
    medium: 'min-h-[160px]',
    large: 'min-h-[240px]'
  }[bannerHeight] || 'min-h-[160px]';
  
  // Recommended sizes
  const sizeLabels = {
    compact: '800 × 100px',
    medium: '800 × 160px', 
    large: '800 × 240px'
  };

  const mediaUrl = current.media_url?.startsWith('/') 
    ? `${process.env.REACT_APP_BACKEND_URL || window.location.origin}${current.media_url}` 
    : current.media_url;

  return (
    <Card 
      className={cn("overflow-hidden relative", className)}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className={cn("relative", heightClass)}>
        {/* Background Media */}
        {hasBackgroundImage && (
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${mediaUrl})` }}
          />
        )}
        
        {hasBackgroundVideo && (
          <video
            ref={videoRef}
            className="absolute inset-0 w-full h-full object-cover"
            src={mediaUrl}
            autoPlay
            loop
            muted={isMuted}
            playsInline
          />
        )}
        
        {/* Color Overlay */}
        {hasMedia && (
          <div 
            className="absolute inset-0"
            style={{ backgroundColor: overlayColor, opacity: overlayOpacity }}
          />
        )}
        
        {/* No media - solid background */}
        {!hasMedia && (
          <div 
            className="absolute inset-0"
            style={{ backgroundColor: bgColor }}
          />
        )}
        
        {/* No media placeholder hint (only shown if no bg color set) */}
        {!hasMedia && bgColor === '#1a365d' && (
          <div className="absolute top-2 right-2 text-xs text-white/40">
            📐 {sizeLabels[bannerHeight]}
          </div>
        )}

        {/* Header with title */}
        <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/50 to-transparent px-4 py-2 z-10">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-sm text-white drop-shadow-md">📢 Flash Infos</h3>
            <div className="flex items-center gap-2">
              {/* Video mute button */}
              {hasBackgroundVideo && (
                <button
                  onClick={toggleMute}
                  className="p-1 rounded-full bg-white/20 hover:bg-white/30 text-white"
                >
                  {isMuted ? (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                    </svg>
                  )}
                </button>
              )}
              
              {/* Pagination dots */}
              {announcements.length > 1 && (
                <div className="flex items-center gap-1.5">
                  {announcements.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => goTo(idx)}
                      className={cn(
                        "w-2 h-2 rounded-full transition-all",
                        idx === currentIndex ? "bg-white scale-125" : "bg-white/40 hover:bg-white/60"
                      )}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Content */}
        <div 
          className="relative z-10 h-full flex flex-col justify-center p-6 pt-12"
          style={{ color: textColor }}
        >
          <h4 className="font-bold text-xl drop-shadow-md">{current.title}</h4>
          {current.content && (
            <p className="text-sm opacity-90 mt-2 drop-shadow-sm max-w-lg">{current.content}</p>
          )}
          
          {current.action_button_text && current.action_button_url && (
            <Button
              size="sm"
              className="mt-4 w-fit backdrop-blur-sm"
              style={{ 
                backgroundColor: `${textColor}20`,
                color: textColor,
                borderColor: textColor
              }}
              variant="outline"
              onClick={() => window.open(current.action_button_url, '_blank')}
            >
              {current.action_button_text}
              <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
        
        {/* Navigation arrows */}
        {announcements.length > 1 && (
          <>
            <button
              onClick={goPrev}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/30 hover:bg-black/50 flex items-center justify-center text-white transition-colors backdrop-blur-sm z-20"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={goNext}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/30 hover:bg-black/50 flex items-center justify-center text-white transition-colors backdrop-blur-sm z-20"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </>
        )}
        
        {/* Progress bar */}
        {announcements.length > 1 && !isPaused && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20 z-20">
            <div 
              className="h-full bg-white/60"
              style={{ 
                width: '100%',
                animation: 'flashInfoProgress 6s linear infinite'
              }}
            />
          </div>
        )}
      </div>
      
      <style>{`
        @keyframes flashInfoProgress {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>
    </Card>
  );
};

// Header Banner Component - Scrolling announcement at top of page
export const HeaderBanner = () => {
  const { isAuthenticated } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dismissed, setDismissed] = useState(new Set());

  useEffect(() => {
    if (!isAuthenticated) return;
    
    const fetchHeaderBanners = async () => {
      try {
        const res = await api.get('/settings/announcements', { params: { display_type: 'header' } });
        setAnnouncements(res.data || []);
      } catch (error) {
        console.error('Error fetching header banners:', error);
      }
    };
    
    fetchHeaderBanners();
  }, [isAuthenticated]);

  // Auto-rotate between banners
  useEffect(() => {
    const activeBanners = announcements.filter(a => !dismissed.has(a.id));
    if (activeBanners.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % activeBanners.length);
    }, 6000);
    
    return () => clearInterval(interval);
  }, [announcements, dismissed]);

  const handleDismiss = (id) => {
    setDismissed(prev => new Set([...prev, id]));
  };

  const activeBanners = announcements.filter(a => !dismissed.has(a.id));
  
  if (activeBanners.length === 0) return null;

  const current = activeBanners[currentIndex % activeBanners.length];
  if (!current) return null;

  const bgColor = current.background_color || '#1a365d';
  const textColor = current.text_color || '#ffffff';

  return (
    <div 
      className="relative overflow-hidden"
      style={{ backgroundColor: bgColor, color: textColor }}
      data-testid="header-banner"
    >
      <div className="max-w-7xl mx-auto px-4 py-2">
        <div className="flex items-center justify-between gap-4">
          {/* Scrolling content */}
          <div className="flex-1 overflow-hidden">
            <div className="animate-marquee whitespace-nowrap flex items-center gap-8">
              <span className="text-sm font-medium">
                {current.title && <strong className="mr-2">{current.title}</strong>}
                {current.content}
              </span>
              {/* Duplicate for seamless loop */}
              <span className="text-sm font-medium">
                {current.title && <strong className="mr-2">{current.title}</strong>}
                {current.content}
              </span>
            </div>
          </div>

          {/* Action button */}
          {current.action_button_text && current.action_button_url && (
            <a
              href={current.action_button_url}
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 px-3 py-1 text-xs font-medium rounded-full transition-all hover:opacity-90"
              style={{ 
                backgroundColor: textColor, 
                color: bgColor 
              }}
            >
              {current.action_button_text}
              <ExternalLink className="w-3 h-3 inline ml-1" />
            </a>
          )}

          {/* Multiple banners indicator */}
          {activeBanners.length > 1 && (
            <div className="flex items-center gap-1 shrink-0">
              {activeBanners.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentIndex(idx)}
                  className={cn(
                    "w-1.5 h-1.5 rounded-full transition-all",
                    idx === currentIndex % activeBanners.length 
                      ? "opacity-100" 
                      : "opacity-40"
                  )}
                  style={{ backgroundColor: textColor }}
                />
              ))}
            </div>
          )}

          {/* Close button */}
          <button
            onClick={() => handleDismiss(current.id)}
            className="shrink-0 p-1 rounded-full hover:bg-white/10 transition-colors"
            style={{ color: textColor }}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Marquee animation style */}
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee 20s linear infinite;
        }
        .animate-marquee:hover {
          animation-play-state: paused;
        }
      `}</style>
    </div>
  );
};

export default { AnnouncementPopup, FlashInfosCarousel, HeaderBanner };
